import React, { useState, useEffect, useMemo } from 'react'
import cn from 'classnames'
import styles from './user-icon.module.scss'
import Bot from 'assets/knov.svg'
import SpaceIcon from 'components/spaces/SpaceIcon'
import { cacheUser, resolveUser } from 'state/cache'
import { queueUserAvatarHydration } from 'api/userAvatarHydration'
import { AVATAR_HYDRATION_GRACE_MS, resolveAvatarRenderMode } from './avatarRenderMode'
import InitialsAvatar from './InitialsAvatar'
import { avatarInitials } from './avatarInitials'

export default function UserIcon(props, ref) {
    const { user, showKnovigator, virtualUserIcon, contextStyles, hoverStyles, selectedStyles } =
        props
    const clickableStyles = props.clickable ? styles.clickable : null
    const [hasAvatarError, setHasAvatarError] = useState(false)
    // While avatar_urls is missing, background bulk hydration should backfill it
    // shortly; hold the /avatar redirect fallback until this grace window ends.
    const [hydrationSettledUserId, setHydrationSettledUserId] = useState(null)
    const [avatarTimestamp, setAvatarTimestamp] = useState(null)
    // Persist most-recent avatar cache key (blob or attachment id)
    const [localAvatarVKey, setLocalAvatarVKey] = useState(null)

    const resolvedUser = useMemo(() => {
        try {
            return resolveUser(user, user?.id)
        } catch {
            return user
        }
    }, [user, avatarTimestamp])
    const avatarSize = props.size ? String(props.size) : 'original'
    const directAvatarUrl =
        resolvedUser?.avatar_urls?.[avatarSize] || resolvedUser?.avatar_urls?.original

    useEffect(() => {
        const handleAvatarUpdate = event => {
            const updatedUserId = event.detail?.user?.id
            const currentUserId = resolvedUser?.id || user?.id

            // Only update if this is the current user's avatar that was updated
            if (updatedUserId && currentUserId && String(updatedUserId) === String(currentUserId)) {
                setAvatarTimestamp(Date.now())
                setHasAvatarError(false)
                const newBlobId = event.detail?.user?.avatar_blob_id
                const newAttachId = event.detail?.user?.avatar_attachment_id
                const newV = newBlobId || newAttachId
                if (newV) {
                    setLocalAvatarVKey(newV)
                    try {
                        localStorage.setItem(`avatar_v:${currentUserId}`, newV)
                    } catch (e) {}
                }
                // Update normalized user cache so all UI reflects the new avatar without
                // needing to invalidate every cached answer.
                try {
                    cacheUser(event.detail.user, true)
                } catch (e) {}
            }
        }

        window.addEventListener('userAvatarUpdated', handleAvatarUpdate)
        return () => window.removeEventListener('userAvatarUpdated', handleAvatarUpdate)
    }, [resolvedUser?.id, user])

    // Virtualized icon instances can be reused for a different user. An image
    // failure only applies to the user/avatar version that produced it.
    useEffect(() => {
        setHasAvatarError(false)
    }, [
        resolvedUser?.id,
        resolvedUser?.avatar_blob_id,
        resolvedUser?.avatar_attachment_id,
        directAvatarUrl,
    ])

    // Stabilize dependency: track whether we have avatar_urls by a boolean, not object reference
    const hasAvatarUrls = Boolean(resolvedUser?.avatar_urls)
    // The API sends avatar_urls: null when the user definitively has no uploaded
    // avatar (vs. the field being absent on stale embedded payloads). That case
    // renders a generated initials avatar with no hydration and no network.
    const avatarKnownMissing = resolvedUser ? resolvedUser.avatar_urls === null : false

    // If a user object is missing avatar_urls (common for embedded/cached Answer payloads),
    // fetch a fresh user record in the background so avatars can render via CDN URLs
    // (and not depend on the /avatar redirect cache). Fetches from all icons mounted
    // in the same tick coalesce into one bulk request; results come back through the
    // 'userAvatarUpdated' event handled above.
    useEffect(() => {
        try {
            const uid = resolvedUser?.id
            if (!uid) return
            if (hasAvatarUrls || avatarKnownMissing) return
            queueUserAvatarHydration(uid)
        } catch (e) {}
    }, [resolvedUser?.id, hasAvatarUrls, avatarKnownMissing])

    // Grace window for the hydration above. While we still lack a CDN url (and
    // haven't errored), show initials; once the window elapses without
    // avatar_urls we let the redirect fallback render. If hydration lands first,
    // hasAvatarUrls flips true and this effect clears the timer before it fires.
    useEffect(() => {
        setHydrationSettledUserId(null)
        if (hasAvatarUrls || hasAvatarError || avatarKnownMissing) return

        const userId = resolvedUser?.id
        if (!userId) return

        const timer = window.setTimeout(
            () => setHydrationSettledUserId(String(userId)),
            AVATAR_HYDRATION_GRACE_MS,
        )
        return () => window.clearTimeout(timer)
    }, [resolvedUser?.id, hasAvatarUrls, hasAvatarError, avatarKnownMissing])

    let userPic

    // Initials are the default avatar. A neutral circle is reserved for the
    // unusual case where no user name is available.
    const renderInitialsAvatar = (fallbackUser, extraClasses = null) =>
        avatarInitials(fallbackUser?.name) ? (
            <div
                className={cn(
                    styles.userIconComp,
                    extraClasses,
                    contextStyles,
                    hoverStyles,
                    selectedStyles,
                )}
            >
                <InitialsAvatar user={fallbackUser} />
            </div>
        ) : (
            <div
                className={cn(
                    styles.userIconComp,
                    styles.userPlaceholder,
                    extraClasses,
                    contextStyles,
                    hoverStyles,
                    selectedStyles,
                )}
                role="img"
                aria-label="User avatar unavailable"
            />
        )

    if (showKnovigator) {
        userPic = (
            <div className="user-icon">
                <SpaceIcon contextStyles={styles.spaceIcon} />
            </div>
        )
    } else if (props.agentIcon) {
        userPic = (
            <div className={cn(styles.userIconComp, contextStyles, hoverStyles, selectedStyles)}>
                <img src={Bot} />
            </div>
        )
    } else if (virtualUserIcon) {
        userPic = (
            <div className={cn(styles.userIconComp, contextStyles, hoverStyles, selectedStyles)}>
                <img src={virtualUserIcon} />
            </div>
        )
    } else if (resolvedUser) {
        // Definitively no uploaded avatar: draw initials locally. No hydration,
        // no /avatar request, and theme switching comes free via CSS variables.
        if (avatarKnownMissing) {
            return <div className="user-icon-comp">{renderInitialsAvatar(resolvedUser)}</div>
        }

        const avatarRenderMode = resolveAvatarRenderMode({
            hasAvatarUrls,
            hasAvatarError,
            userId: resolvedUser.id,
            hydrationSettledUserId,
        })

        // A failed source is terminal for this avatar version. Do not retry the
        // same URL or swap between CDN and redirect sources indefinitely.
        if (avatarRenderMode === 'initials') {
            return <div className="user-icon-comp">{renderInitialsAvatar(resolvedUser)}</div>
        }

        // Prefer direct CDN URLs when provided by the API.
        // This avoids hitting the Rails avatar redirect endpoint for every icon.
        if (avatarRenderMode === 'cdn') {
            try {
                if (directAvatarUrl) {
                    userPic = (
                        <div
                            className={cn(
                                styles.userIconComp,
                                contextStyles,
                                hoverStyles,
                                selectedStyles,
                            )}
                        >
                            <img
                                key={`${directAvatarUrl}:${avatarTimestamp || ''}`}
                                src={directAvatarUrl}
                                className={styles.userImage}
                                onError={() => setHasAvatarError(true)}
                            />
                        </div>
                    )
                }
            } catch (e) {}

            if (userPic) return <div className="user-icon-comp">{userPic}</div>
        }

        // No CDN url yet. While bulk hydration is still within its grace window,
        // show initials instead of firing the /avatar redirect (the N+1).
        if (avatarRenderMode === 'awaiting-hydration') {
            return <div className="user-icon-comp">{renderInitialsAvatar(resolvedUser)}</div>
        }

        // Build a cache-aware avatar URL using a blob-scoped key when available
        let storedAttachmentId = null
        try {
            storedAttachmentId = resolvedUser?.id
                ? localStorage.getItem(`avatar_v:${resolvedUser.id}`)
                : null
        } catch (e) {}
        const blobId = resolvedUser?.avatar_blob_id || localAvatarVKey || storedAttachmentId
        // Fallback to attachment id, then updatedAt, then user id
        const attachmentId = resolvedUser?.avatar_attachment_id
        const vKey = blobId || attachmentId || props.updatedAt || resolvedUser?.id

        const qs = []
        if (vKey) qs.push(`v=${encodeURIComponent(vKey)}`)
        if (props.size) qs.push(`size=${encodeURIComponent(props.size)}`)
        // When an avatar update event occurs in-session, force a one-off cache break
        if (avatarTimestamp) qs.push('break_cache=true')
        if (avatarTimestamp) qs.push(`cb=${encodeURIComponent(avatarTimestamp)}`)

        const queryString = qs.length ? `?${qs.join('&')}` : ''
        const avatarUrl = `${window.knovApiUrl}/api/v1/users/${resolvedUser.id}/avatar${queryString}`
        userPic = (
            <div className={cn(styles.userIconComp, contextStyles, hoverStyles, selectedStyles)}>
                <img
                    key={avatarUrl}
                    src={avatarUrl}
                    className={styles.userImage}
                    onError={() => setHasAvatarError(true)}
                />
            </div>
        )
    } else if (user?.name) {
        userPic = renderInitialsAvatar(user, clickableStyles)
    } else {
        userPic = (
            <div
                className={cn(contextStyles, hoverStyles, selectedStyles, styles.userPlaceholder)}
            />
        )
    }

    return <div className="user-icon-comp">{userPic}</div>
}
